import React, { useState, useEffect, useRef } from 'react';
import { db } from '../../../utils/firebase-config';
import { doc, getDoc, setDoc, updateDoc, collection, getDocs } from 'firebase/firestore';
import wealthBuildingSteps from '../Questions/WealthBuildingStage';
import StepHeader from '../StepHeader';
import updateUserProgress from '../userProgressTrack/updateUserProgress';
import { useNavigate } from 'react-router-dom';
import AdjustmentForm from './AdjustmentForm'; // Import the new component
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import SummaryTable from './SummaryTable';

const WealthBuildingForm = ({ userId, currentStep, handleNextStep, handlePreviousStep, area, nextStep }) => {
  const currentStepData = wealthBuildingSteps.find((step) => step.id === currentStep);
  const collectionName = currentStepData.stage;

  const [formValues, setFormValues] = useState({});
  const [tableData, setTableData] = useState({});
  const [summaryData, setSummaryData] = useState(null);
  const [showSummary, setShowSummary] = useState(false);
  const [showAdjustmentForm, setShowAdjustmentForm] = useState(false);
  const [adjustmentValues, setAdjustmentValues] = useState({});
  const [selectedFields, setSelectedFields] = useState([]);
  const [allUserData, setAllUserData] = useState([]);
  const [summaryType, setSummaryType] = useState(currentStepData?.summaryType || 'Past');
  const [hasStartedForm, setHasStartedForm] = useState(false);
  const dataFetched = useRef(false);
  const isFormModified = useRef(false);
  const [showReviewPopup, setShowReviewPopup] = useState(false);
  const isLastStep = currentStepData.lastStep;
  const navigate = useNavigate();
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [nextSummaryData, setNextSummaryData] = useState(null);
  const [loadingNextSummary, setLoadingNextSummary] = useState(true);
  

  const [openSections, setOpenSections] = useState({});
  const [selectedTab, setSelectedTab] = useState('Past'); // State for managing tabs


  const toggleSection = (sectionKey) => {
    setOpenSections((prevState) => ({
      ...prevState,
      [sectionKey]: !prevState[sectionKey],
    }));
  };

  const handleTabChange = (tab) => {
    setSelectedTab(tab);
  };

  // Updated helper function to sanitize field names
  const sanitizeFieldName = (name) => name.replace(/[~*/[\].,]/g, '_').replace(/\s+/g, '_');

  
  const beautifyFieldName = (field) => {
    return field
    .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/_/g, ' ') // Replace underscores with spaces
      .replace(/\s+/g, ' ') // Replace multiple spaces with a single space
      .replace(/\b\w/g, (char) => char.toUpperCase()) // Capitalize first letter of each word
      .trim(); // Remove leading/trailing spaces
  };
  
  
  // Retrieve `showSummary` from localStorage on component mount
  useEffect(() => {
    const savedShowSummary = localStorage.getItem('showSummary');
    const savedSummaryData = localStorage.getItem('summaryData');

    if (savedShowSummary === 'true' && savedSummaryData) {
      setShowSummary(true);
      setSummaryData(JSON.parse(savedSummaryData)); // Load the saved summary data
    }
  }, []);

  // Function to save summary state in localStorage
  const saveSummaryState = (data) => {
    localStorage.setItem('showSummary', 'true');
    localStorage.setItem('summaryData', JSON.stringify(data)); // Save summary data
  };

  useEffect(() => {
    const fetchSummaryData = async () => {
      setLoadingSummary(true);
  
      if (!userId) {
        console.error("Missing userId");
        return;
      }
  
      try {
        const stages = ['Past', 'Next'];
        let combinedSummary = {
          needs: { ideal: 0, actual: 0 },
          wants: { ideal: 0, actual: 0 },
          savings: { ideal: 0, actual: 0 },
        };
  
        for (const stage of stages) {
          const stageRef = collection(db, 'users', userId, 'Wealth_Building_Stage', collectionName, stage);
          const stageSnapshot = await getDocs(stageRef);
  
          stageSnapshot.forEach((doc) => {
            const data = doc.data();
  
            console.log(`Fetched Data for Stage ${stage}:`, data);
  
            // Validate and parse values
            const stageSummary = {
              needs: {
                ideal: parseFloat(data.summary?.needs?.ideal || '0'),
                actual: parseFloat(data.Needs_Necessities_?.expensesMonthly || '0'),
              },
              wants: {
                ideal: parseFloat(data.summary?.wants?.ideal || '0'),
                actual: parseFloat(data.Wants_Luxuries_?.expensesMonthly || '0'),
              },
              savings: {
                ideal: parseFloat(data.summary?.savings?.ideal || '0'),
                actual: parseFloat(data.Savings_and_Debt_Repayment?.expensesMonthly || '0'),
              },
            };
  
            // Accumulate values
            combinedSummary.needs.ideal += stageSummary.needs.ideal;
            combinedSummary.needs.actual += stageSummary.needs.actual;
            combinedSummary.wants.ideal += stageSummary.wants.ideal;
            combinedSummary.wants.actual += stageSummary.wants.actual;
            combinedSummary.savings.ideal += stageSummary.savings.ideal;
            combinedSummary.savings.actual += stageSummary.savings.actual;
  
            console.log(`Accumulating ${stage}:`, combinedSummary);

          });
        }
  
        console.log('Final Combined Summary:', combinedSummary);
        setSummaryData(combinedSummary);
      } catch (error) {
        console.error('Failed to fetch summary data:', error);
      } finally {
        setLoadingSummary(false);
      }
    };
  
    fetchSummaryData();
  }, [userId, collectionName]);
  

  useEffect(() => {
    const fetchAllUserData = async () => {
      try {
        const allData = {};
        const stages = ['Past', 'Next']; 
  
        for (const stage of stages) {
          const stageRef = collection(db, 'users', userId, 'Wealth_Building_Stage', collectionName, stage);
          const stageSnapshot = await getDocs(stageRef);
  
          if (!allData[stage]) {
            allData[stage] = [];
          }
  
          stageSnapshot.forEach((doc) => {
            const data = doc.data();
            allData[stage].push(data);
  
            if (stage === 'Next') {
              const nextValues = {};
              
              // Fetch fields directly as they are from the database structure
              ['Needs_Necessities', 'Savings_And_Debt_Repayment', 'Wants_Luxuries'].forEach((category) => {
                if (data[category]) {
                  Object.keys(data[category]).forEach((fieldName) => {
                    // Use the field names directly from the database format
                    nextValues[`${category}.${fieldName}`] = { value: data[category][fieldName] };
                  });
                }
              });
              
              setAdjustmentValues(nextValues);
              //console.log("Populated adjustmentValues with 'Next' data:", nextValues);
            }
          });
        }
  
        setAllUserData(allData);
      } catch (error) {
        console.error('Failed to fetch all user data:', error);
      }
    };
  
    fetchAllUserData();
  }, [userId, collectionName]);
  

  // Function to sanitize the stepTitle to match the Firestore key format
const sanitizeStepTitle = (title) => {
  return title.replace(/[~*/[\].,]/g, '_').replace(/\s+/g, '_');
};


  //Output already filled data in the fields FOR PAST Table

  useEffect(() => {
    const fetchFormValues = async () => {
      try {
        // Reference to the correct path in Firestore
        const docRef = doc(db, 'users', userId, 'Wealth_Building_Stage', collectionName, 'Past', 'data');
        const docSnap = await getDoc(docRef);
  
        if (docSnap.exists()) {
          const data = docSnap.data();
          console.log('Fetched data from Firestore:', data); // Log the raw fetched data
  
          const initialFormValues = {};
  
          if (currentStepData && currentStepData.formFields) {
            currentStepData.formFields.forEach((field) => {
              // Check each nested section for the field ID
              for (const sectionKey of ['Needs_Necessities_', 'Savings_And_Debt_Repayment', 'Wants_Luxuries_']) {
                if (data[sectionKey] && data[sectionKey][field.id] !== undefined) {
                  
                  //console.log(`Found field ${field.id} in section ${sectionKey} with value: ${data[sectionKey][field.id]}`);
                  initialFormValues[field.id] = data[sectionKey][field.id];
                }
              }
  
              // Also check for top-level properties if they exist
              if (data[field.id] !== undefined) {
                //console.log(`Found field ${field.id} at the top level with value: ${data[field.id]}`);
                initialFormValues[field.id] = data[field.id];
              }
            });
  
            // Generate the dynamic key for the nested object using the sanitized stepTitle
            const dynamicStepKey = sanitizeStepTitle(currentStepData.stepTitle);
            if (data[dynamicStepKey]) {
              const incomeSection = data[dynamicStepKey];
  
              if (incomeSection.incomeAfterTax !== undefined) {
                initialFormValues.incomeAfterTax = incomeSection.incomeAfterTax;
                console.log('Found incomeAfterTax in nested section:', incomeSection.incomeAfterTax);
              }
              if (incomeSection.additionalIncome !== undefined) {
                initialFormValues.additionalIncome = incomeSection.additionalIncome;
                console.log('Found additionalIncome in nested section:', incomeSection.additionalIncome);
              }
            } else {
              console.warn(`Nested section ${dynamicStepKey} not found in Firestore data`);
            }
          } else {
            console.warn('No form fields found for the current step.');
          }
  
          // Log the final form values before setting state
          //console.log('Initial form values prepared:', initialFormValues);
          setFormValues(initialFormValues);
          dataFetched.current = true;
        } else {
          console.warn('No existing data found in Firestore.');
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
    };
  
    // Load form values from localStorage if available, otherwise fetch from Firestore
    const savedFormValues = localStorage.getItem(`wealthForm_${userId}_${currentStep}`);
    if (savedFormValues) {
      //console.log('Loaded form values from localStorage:', JSON.parse(savedFormValues)); // Log loaded values
      setFormValues(JSON.parse(savedFormValues));
      dataFetched.current = true;
    } else {
      fetchFormValues(); // Call the function defined above
    }
  }, [userId, currentStep, currentStepData, collectionName]);
  
  

  useEffect(() => {
    const calculateMonthlyAndPercentages = () => {
      const totalIncome = parseFloat(formValues.incomeAfterTax || 0) + parseFloat(formValues.additionalIncome || 0);
      let totalExpenses = 0;

      currentStepData.formFields.forEach((field) => {
        const fieldValue = parseFloat(formValues[field.id]) || 0;
        if (field.id !== 'incomeAfterTax' && field.id !== 'additionalIncome') {
          totalExpenses += fieldValue;
        }
      });

      const sumOfPercentages = totalIncome > 0 && totalExpenses > 0 ? (totalExpenses / totalIncome) * 100 : 0;

      const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
        }).format(value);
      };

      const tableData = {
        incomeAfterTaxMonthly: formatCurrency(formValues.incomeAfterTax / 12 || 0),
        additionalIncomeMonthly: formatCurrency(formValues.additionalIncome / 12 || 0),
        totalIncomeMonthly: formatCurrency(totalIncome / 12 || 0),
        incomeAfterTaxPercentage: totalIncome > 0 ? ((formValues.incomeAfterTax / totalIncome) * 100).toFixed(2) : '0',
        additionalIncomePercentage: totalIncome > 0 ? ((formValues.additionalIncome / totalIncome) * 100).toFixed(2) : '0',
        totalIncomePercentage: totalIncome > 0 ? 100 : '0',
        totalExpenses,
        expensesMonthly: formatCurrency(totalExpenses / 12 || 0),
        totalIncome: formatCurrency(totalIncome || 0),
        totalExpensesPercentage: totalIncome > 0 ? sumOfPercentages.toFixed(2) : '0',
      };

      setTableData(tableData);

      if (isLastStep) {
        const summary = calculateSummaryTable(tableData);
        setSummaryData(summary);
      }
    };

    calculateMonthlyAndPercentages();
  }, [formValues, currentStepData, isLastStep]);


  const calculateSummaryTable = (tableData, existingSummary = {}) => {
    const totalIncome = parseFloat(tableData?.totalIncome?.replace(/[^0-9.-]+/g, "")) || 0;
    const totalIncomeMonthly = parseFloat(tableData?.totalIncomeMonthly?.replace(/[^0-9.-]+/g, "")) || 0;
    
  
    // Ideal values based on the 50/30/20 rule
    const idealNeedsMax = totalIncomeMonthly * 0.5 || parseFloat(existingSummary?.needs?.ideal) || 0;
    const idealWantsMax = totalIncomeMonthly * 0.3 || parseFloat(existingSummary?.wants?.ideal) || 0;
    const idealSavingsMin = totalIncomeMonthly * 0.2 || parseFloat(existingSummary?.savings?.ideal) || 0;
  
    // Actual values derived from `tableData` or fallback to existing summary
    const actualNeeds = parseFloat(tableData?.needs?.actual || existingSummary?.needs?.actual || 0);
    const actualWants = parseFloat(tableData?.wants?.actual || existingSummary?.wants?.actual || 0);
    const actualSavings = parseFloat(tableData?.savings?.actual || existingSummary?.savings?.actual || 0);
  
    // Debugging logs to verify inputs
    console.log("Summary Calculation Inputs:", { totalIncome, tableData, existingSummary });
    console.log("Ideal Values Calculated:", { idealNeedsMax, idealWantsMax, idealSavingsMin });
    console.log("Actual Values Calculated:", { actualNeeds, actualWants, actualSavings });
  
    return {
      needs: {
        ideal: idealNeedsMax.toFixed(2),
        actual: actualNeeds.toFixed(2),
      },
      wants: {
        ideal: idealWantsMax.toFixed(2),
        actual: actualWants.toFixed(2),
      },
      savings: {
        ideal: idealSavingsMin.toFixed(2),
        actual: actualSavings.toFixed(2),
      },
    };
  };
  
  
  
  
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    const parsedValue = value !== '' ? parseFloat(value) : '';  // Avoid defaulting to 0 for empty inputs
    
    setFormValues((prevValues) => {
      const updatedFormValues = {
        ...prevValues,
        [name]: parsedValue
      };
    
      // Save to localStorage on each change
      localStorage.setItem(`wealthForm_${userId}_${currentStep}`, JSON.stringify(updatedFormValues));
  
      return updatedFormValues;
    });
  
    isFormModified.current = true;
  };
  

  //DEBUG
  // useEffect(() => {
  //   console.log('Form Values:', formValues);  // Debugging statement to see form values
  // }, [formValues]);


  
  // console.log('Current Step Data:', currentStepData);  // Check if step data is correctly loaded
  // console.log('Wealth Building Steps:', wealthBuildingSteps);  // Check the source of steps
  
  const handleAdjustFields = () => {
    const selectedFieldKeys = selectedFields;

    // Create the adjustment values object
    const newAdjustmentValues = selectedFieldKeys.reduce((acc, key) => {
      const fieldData = currentStepData.formFields.find((field) => field.id === key);

      const fieldValue = formValues[key] !== undefined ? formValues[key] : 0; // Default to 0 if value is undefined

      acc[key] = {
        value: fieldValue,  // Keep the form value intact
        title: fieldData ? fieldData.title : key,
      };

      return acc;
    }, {});

    console.log('Adjustment Values in handleAdjustFields:', newAdjustmentValues);
    
    setAdjustmentValues(newAdjustmentValues); // Set adjustment values in state
    setShowAdjustmentForm(true); // Display the adjustment form popup
  };
  
  


  const handleAdjustmentChange = (e) => {
    const { name, value } = e.target;
    const parsedValue = value !== '' ? parseFloat(value) : 0;
  
    setAdjustmentValues((prevValues) => ({
      ...prevValues,
      [name]: {
        ...prevValues[name],
        value: parsedValue,
      },
    }));
  };
  

  useEffect(() => {
    const loadAdjustmentValues = async () => {
      try {
        const storedAdjustmentValues = JSON.parse(localStorage.getItem('adjustmentValues') || '{}');
        let loadedAdjustmentValues = {};
  
        if (Object.keys(storedAdjustmentValues).length > 0) {
          console.log('Loaded adjustment values from local storage:', storedAdjustmentValues);
          loadedAdjustmentValues = { ...storedAdjustmentValues };
        } else {
          const nextRef = doc(db, 'users', userId, 'Wealth_Building_Stage', collectionName, 'Next', 'data');
          const nextSnapshot = await getDoc(nextRef);
  
          if (nextSnapshot.exists()) {
            const data = nextSnapshot.data();
  
            // Iterate through expected categories and populate adjustmentValues
            ['Needs_Necessities_', 'Savings_And_Debt_Repayment', 'Wants_Luxuries_'].forEach((category) => {
              if (data[category]) {
                Object.keys(data[category]).forEach((fieldKey) => {
                  const combinedKey = `${category}.${fieldKey}`;
                  loadedAdjustmentValues[combinedKey] = {
                    value: data[category][fieldKey] !== undefined ? data[category][fieldKey] : 0,
                  };
                });
              }
            });
  
            console.log('Loaded adjustment values from Firestore:', loadedAdjustmentValues);
            localStorage.setItem('adjustmentValues', JSON.stringify(loadedAdjustmentValues)); // Save to localStorage
          } else {
            console.warn('No Next data found in Firestore.');
          }
        }
  
        setAdjustmentValues(loadedAdjustmentValues);
      } catch (error) {
        console.error('Error loading adjustment values:', error);
      }
    };
  
    loadAdjustmentValues();
  }, [userId, collectionName]);
  
  
  
  const saveAdjustments = async () => {
    try {
      console.log('Saving adjustments:', adjustmentValues);
  
      const userRef = doc(db, 'users', userId);
      const wealthBuildingStageRef = doc(userRef, 'Wealth_Building_Stage', collectionName);
      const subCollectionRef = doc(wealthBuildingStageRef, 'Next', 'data');
  
      const categorizedData = {
        Wants_Luxuries: {},
        Needs_Necessities: {},
        Savings_And_Debt_Repayment: {},
      };
  
      // Iterate over adjustmentValues and categorize them correctly
      Object.keys(adjustmentValues).forEach((fieldId) => {
        const value = adjustmentValues[fieldId]?.value;
        if (value !== undefined && value !== null) {
          let [categoryKey, fieldName] = fieldId.split('.');
  
          // Normalize categoryKey
          categoryKey = categoryKey.replace(/_+$/, ''); // Remove trailing underscores
          if (categoryKey === 'Savings_and_Debt_Repayment') {
            categoryKey = 'Savings_And_Debt_Repayment'; // Ensure consistent capitalization
          }
  
          // Correct category mapping with normalized keys
          if (categorizedData[categoryKey]) {
            categorizedData[categoryKey][fieldName] = value;
          } else {
            console.warn(`Unknown category: ${categoryKey}`);
          }
        }
      });
  
      // Check if there is any data to save
      const hasData =
        Object.keys(categorizedData.Wants_Luxuries).length > 0 ||
        Object.keys(categorizedData.Needs_Necessities).length > 0 ||
        Object.keys(categorizedData.Savings_And_Debt_Repayment).length > 0;
  
      if (hasData) {
        await setDoc(subCollectionRef, categorizedData, { merge: true });
        console.log('Data saved to Firestore:', categorizedData);
  
        // Update local storage to ensure consistency
        localStorage.setItem('adjustmentValues', JSON.stringify(adjustmentValues));
  
        toast.success('Adjustments saved successfully!', {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      } else {
        console.warn('No data to save.');
      }
    } catch (error) {
      console.error('Failed to save adjustments:', error);
      toast.error('There was an issue saving your adjustments.', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }
  };
  
  
  

  // Calculate summary specifically for the Next section
  const calculateNextSummary = (adjustmentValues) => {
    const totalIncome = parseFloat(tableData.totalIncome.replace(/[^0-9.-]+/g, "")) || 0;
  
    const summary = {
      needs: {
        ideal: totalIncome * 0.5,
        actual: 0,
      },
      wants: {
        ideal: totalIncome * 0.3,
        actual: 0,
      },
      savings: {
        ideal: totalIncome * 0.2,
        actual: 0,
      },
    };
  
    Object.keys(adjustmentValues).forEach((fieldId) => {
      const value = adjustmentValues[fieldId]?.value || 0;
      let categoryKey = fieldId.split('.')[0].replace(/_+$/, ''); // Normalize trailing underscores
  
      if (categoryKey === 'Savings_and_Debt_Repayment') {
        categoryKey = 'Savings_And_Debt_Repayment';
      }
  
      if (categoryKey === 'Needs_Necessities') {
        summary.needs.actual += value;
      } else if (categoryKey === 'Wants_Luxuries') {
        summary.wants.actual += value;
      } else if (categoryKey === 'Savings_And_Debt_Repayment') {
        summary.savings.actual += value;
      }
    });
  
    return summary;
  };
  

//Past
  useEffect(() => {
    const fetchAndCalculateNextSummary = async () => {
      try {
        const nextRef = doc(db, 'users', userId, 'Wealth_Building_Stage', collectionName, 'Next', 'data');
        const nextSnapshot = await getDoc(nextRef);
  
        if (nextSnapshot.exists()) {
          const data = nextSnapshot.data();
          
          // Assuming `totalIncomeMonthly` is either stored in this document or calculated elsewhere
          const totalIncomeMonthly = parseFloat(data.totalIncomeMonthly || 0);
          
          const summaryData = {
            needs: {
              ideal: totalIncomeMonthly * 0.5,
              actual: data.Needs_Necessities ? Object.values(data.Needs_Necessities).reduce((acc, val) => acc + val, 0) : 0,
            },
            wants: {
              ideal: totalIncomeMonthly * 0.3,
              actual: data.Wants_Luxuries ? Object.values(data.Wants_Luxuries).reduce((acc, val) => acc + val, 0) : 0,
            },
            savings: {
              ideal: totalIncomeMonthly * 0.2,
              actual: data.Savings_And_Debt_Repayment ? Object.values(data.Savings_And_Debt_Repayment).reduce((acc, val) => acc + val, 0) : 0,
            },
          };
  
          setNextSummaryData(summaryData);
        } else {
          console.warn('No Next data found in Firestore.');
        }
      } catch (error) {
        console.error('Failed to fetch and calculate Next summary data:', error);
      }
    };
  
    fetchAndCalculateNextSummary();
  }, [userId, collectionName]);
  


const calculateTotalForSelectedFields = (category) => {
  let total = 0;
  let totalMonthly = 0;

  selectedFields.forEach((field) => {
    // Check if the field starts with the category (like Needs_Necessities_)
    if (field.startsWith(category)) {
      const fieldValue = adjustmentValues[field]?.value || 0;
      total += fieldValue;
      totalMonthly += fieldValue / 12; // Assuming the values are annual
    }
  });

  const totalPercentage = total > 0 && tableData.totalExpenses > 0
    ? (total / tableData.totalExpenses) * 100
    : 0;

  return {
    total: total.toFixed(2),
    totalMonthly: totalMonthly.toFixed(2),
    totalPercentage: totalPercentage.toFixed(2),
  };
};


const handleSubmit = async (e) => {
  e.preventDefault();

  const newFormValues = {};
  const formFields = currentStepData.formFields;
  const sanitizedStepTitle = sanitizeFieldName(currentStepData.stepTitle);

  // Gather form values
  for (const field of formFields) {
    const fieldValue = formValues[field.id];
    if (!fieldValue && fieldValue !== 0) {
      setShowReviewPopup(true);
      setTimeout(() => setShowReviewPopup(false), 3000);
      return;
    }
    newFormValues[field.id] = fieldValue;
  }

  // Parse income/expenses
  const parsedTotalIncome = parseFloat(tableData?.totalIncome?.replace(/[^0-9.-]+/g, "")) || 0;
  const parsedTotalIncomeMonthly = parseFloat(tableData?.totalIncomeMonthly?.replace(/[^0-9.-]+/g, "")) || 0;
  const parsedExpensesMonthly = parseFloat(tableData?.expensesMonthly?.replace(/[^0-9.-]+/g, "")) || 0;

  // Map actual values
  const actualNeeds = parseFloat(formValues.needs || 0);
  const actualWants = parseFloat(formValues.wants || 0);
  const actualSavings = parseFloat(formValues.savings || 0);

  // Update tableData with actual values
  tableData.needs = { actual: actualNeeds };
  tableData.wants = { actual: actualWants };
  tableData.savings = { actual: actualSavings };

  newFormValues.totalIncome = parsedTotalIncome;
  newFormValues.totalIncomeMonthly = parsedTotalIncomeMonthly;
  newFormValues.expensesMonthly = parsedExpensesMonthly;

  try {
    const userRef = doc(db, 'users', userId);
    const wealthBuildingStageRef = doc(userRef, 'Wealth_Building_Stage', collectionName);
    const subCollectionName = summaryType === 'Past' ? 'Past' : 'Next';
    const subCollectionRef = doc(wealthBuildingStageRef, subCollectionName, 'data');

    const subCollectionSnap = await getDoc(subCollectionRef);
    const existingData = subCollectionSnap.exists() ? subCollectionSnap.data() : {};
    const existingSummary = existingData.summary || {};

    // Calculate updated summary
    const updatedSummary = calculateSummaryTable(tableData, existingSummary);

    console.log("Updated Summary Before Saving:", updatedSummary);

    await setDoc(
      subCollectionRef,
      {
        totalIncome: parsedTotalIncome,
        totalIncomeMonthly: parsedTotalIncomeMonthly,
        expensesMonthly: parsedExpensesMonthly,
        [sanitizedStepTitle]: newFormValues,
        summary: updatedSummary,
        summaryType,
        stepId: currentStep,
      },
      { merge: true }
    );

    if (isLastStep) {
      setShowSummary(true);
      saveSummaryState(updatedSummary);
    } else {
      const progressData = {
        [`progress.${area}`]: currentStep + 1,
        currentStep: currentStep + 1,
      };
      await updateDoc(userRef, progressData);
      handleNextStep();
    }
  } catch (error) {
    console.error("Failed to save data:", error);
  }
};



  const hideSummary = () => {
    setShowSummary(false);
    localStorage.removeItem('showSummary');
    //localStorage.removeItem('summaryData');
  };

  const handleBack = async () => {
    try {
      const previousStep = currentStep - 1;
      const userRef = doc(db, 'users', userId);

      const progressData = {
        [`progress.${area}`]: previousStep,
        currentStep: previousStep,
      };

      await updateDoc(userRef, progressData);
      handlePreviousStep();
    } catch (error) {
      console.error('Failed to update progress on back:', error);
    }
  };

  useEffect(() => {
    console.log("Current step changed to:", currentStep);
    
    // Add conditions here to check where the redirect to step 24 might occur
  }, [currentStep]);
  

  const handleRedirectToStart = async () => {
    const startStep = 21;
    
    try {
      console.log("Redirecting to start step:", startStep);
      if (!userId) {
        throw new Error('User ID is undefined');
      }
  
      // Clear localStorage and reset summary view
      localStorage.removeItem('showSummary');
      //localStorage.removeItem('summaryData');
      //localStorage.removeItem(`wealthForm_${userId}_${currentStep}`);
      setShowSummary(false);
  
      // Update Firestore to step 21
      await updateUserProgress(userId, 'Wealth Building Stage', startStep);
      await updateDoc(doc(db, 'users', userId), { currentStep: startStep });
  
      // Double-check if the update is successful
      const updatedDoc = await getDoc(doc(db, 'users', userId));
      console.log("Firestore currentStep after update:", updatedDoc.data().currentStep);
  
      // Navigate to the start step
      //navigate(`/survey/${startStep}`, { replace: true });
      handleNextStep();
    } catch (error) {
      console.error("Failed to redirect to start:", error);
    }
  };
  
  
  
  const handleCheckboxChange = async (field) => {
    setSelectedFields((prevSelectedFields) => {
      // Check if the field is already in the selectedFields array
      const isAlreadySelected = prevSelectedFields.includes(field);
      const updatedSelectedFields = isAlreadySelected
        ? prevSelectedFields.filter((f) => f !== field) // Remove field if already selected
        : [...prevSelectedFields, field]; // Add field if not already selected
  
      // Update adjustmentValues accordingly
      setAdjustmentValues((prevAdjustmentValues) => {
        if (isAlreadySelected) {
          // Remove field from adjustmentValues if it was already selected
          const { [field]: _, ...rest } = prevAdjustmentValues;
          return rest;
        }
        // Add field to adjustmentValues with an initial value if not already present
        return {
          ...prevAdjustmentValues,
          [field]: prevAdjustmentValues[field] || { value: '' },
        };
      });
  
      // Persist the updated selectedFields to localStorage
      localStorage.setItem('selectedFields', JSON.stringify(updatedSelectedFields));
  
      // Call createNextAreaInFirestore only if the field was just added
      if (!isAlreadySelected) {
        createNextAreaInFirestore(field);
      }
  
      return updatedSelectedFields;
    });
  
    // Use functional update to ensure the latest selectedFields value is used
    setSelectedFields((prev) => {
      if (!prev.includes(field)) {
        toast.error(`${beautifyFieldName(field.split('.')[1])} removed from Next 12 Months Table`);
      } else {
        toast.success(`${beautifyFieldName(field.split('.')[1])} added to Next 12 Months Table`);

      }
      return prev;
    });
  };

  // useEffect(() => {
  //   console.log('Current selectedFields:', selectedFields);
  // }, [selectedFields]);
  
  useEffect(() => {
    const initializeCheckboxState = async () => {
      try {
        // Check localStorage first
        const storedSelectedFields = JSON.parse(localStorage.getItem('selectedFields') || '[]');
        if (storedSelectedFields.length > 0) {
          setSelectedFields(storedSelectedFields);
          return; // Skip Firestore fetch if localStorage exists
        }
  
        // Fetch data from Firestore if localStorage is empty
        const nextRef = doc(db, 'users', userId, 'Wealth_Building_Stage', collectionName, 'Next', 'data');
        const nextSnapshot = await getDoc(nextRef);
  
        if (nextSnapshot.exists()) {
          const data = nextSnapshot.data();
          const fieldsWithValues = [];
  
          // Iterate through categories and find fields with non-empty values
          ['Needs_Necessities', 'Savings_And_Debt_Repayment', 'Wants_Luxuries'].forEach((category) => {
            if (data[category]) {
              Object.entries(data[category]).forEach(([fieldKey, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                  fieldsWithValues.push(`${category}.${fieldKey}`);
                }
              });
            }
          });
  
          // Update selectedFields and persist to localStorage
          setSelectedFields(fieldsWithValues);
          localStorage.setItem('selectedFields', JSON.stringify(fieldsWithValues));
        }
      } catch (error) {
        console.error('Error initializing checkbox state:', error);
      }
    };
  
    initializeCheckboxState();
  }, [userId, collectionName]);
  
  
  
  
  const createNextAreaInFirestore = async (field) => {
    try {
      const userRef = doc(db, 'users', userId);
      const wealthBuildingStageRef = doc(userRef, 'Wealth_Building_Stage', collectionName);
      const subCollectionRef = doc(wealthBuildingStageRef, 'Next', 'data');
  
      const docSnap = await getDoc(subCollectionRef);
      if (!docSnap.exists()) {
        // Create the document with an initial structure similar to 'Past'
        await setDoc(subCollectionRef, {
          Needs_Necessities: {},
          Savings_And_Debt_Repayment: {},
          Wants_Luxuries: {},
        }, { merge: true });
        console.log("'Next' area created in Firestore with initial structure");
      }
  
      // Add the field to the appropriate section
      const [categoryKey, fieldName] = field.split('.');
      if (['Needs_Necessities', 'Savings_And_Debt_Repayment', 'Wants_Luxuries'].includes(categoryKey)) {
        await updateDoc(subCollectionRef, {
          [`${categoryKey}.${fieldName}`]: 0 // Initialize with a value of 0
        });
        console.log(`Field ${fieldName} added to ${categoryKey} in Firestore 'Next' area`);
      }
    } catch (error) {
      console.error('Failed to create or update the Next area in Firestore:', error);
    }
  };
  
  

    // Retrieve saved checkboxes state on component mount
    useEffect(() => {
      // Load selectedFields from localStorage only if not already populated
      const savedSelectedFields = JSON.parse(localStorage.getItem('selectedFields') || '[]');
      
      if (savedSelectedFields.length > 0) {
        setSelectedFields(savedSelectedFields);
      } else if (Object.keys(adjustmentValues).length > 0) {
        // Only populate selectedFields from adjustmentValues if localStorage is empty
        const initialFields = Object.keys(adjustmentValues);
        setSelectedFields(initialFields);
        localStorage.setItem('selectedFields', JSON.stringify(initialFields));
      }
    }, [adjustmentValues]);
    

      

  // Save checked state to localStorage whenever selectedFields changes
  useEffect(() => {
    if (selectedFields.length > 0) {
      localStorage.setItem('selectedFields', JSON.stringify(selectedFields));
    }
  }, [selectedFields]);

  
  
  useEffect(() => {
    const fetchNextData = async () => {
      try {
        const nextRef = doc(db, 'users', userId, 'Wealth_Building_Stage', collectionName, 'Next', 'data');
        const nextSnapshot = await getDoc(nextRef);
  
        if (nextSnapshot.exists()) {
          const data = nextSnapshot.data();
          const loadedAdjustmentValues = {};
  
          // Iterate through all expected categories and fields
          const expectedCategories = ['Needs_Necessities', 'Savings_And_Debt_Repayment', 'Wants_Luxuries'];
          expectedCategories.forEach((category) => {
            if (data[category]) {
              Object.keys(data[category]).forEach((fieldKey) => {
                const combinedKey = `${category}.${fieldKey}`;
                loadedAdjustmentValues[combinedKey] = {
                  value: data[category][fieldKey] !== undefined ? data[category][fieldKey] : 0, // Set to 0 if undefined
                };
              });
            }
          });
  
          console.log("Loaded 'Next' data into adjustmentValues:", loadedAdjustmentValues);
          setAdjustmentValues(loadedAdjustmentValues);
          localStorage.setItem('adjustmentValues', JSON.stringify(loadedAdjustmentValues)); // Save to local storage
        } else {
          console.warn('No Next data found in Firestore.');
        }
      } catch (error) {
        console.error('Error fetching Next data from Firestore:', error);
      }
    };
  
    // Load from Firestore and also check local storage for existing data
    const storedAdjustmentValues = JSON.parse(localStorage.getItem('adjustmentValues') || '{}');
    if (Object.keys(storedAdjustmentValues).length > 0) {
      setAdjustmentValues(storedAdjustmentValues);
      console.log('Loaded adjustment values from local storage:', storedAdjustmentValues);
    } else {
      fetchNextData(); // Fetch from Firestore if local storage is empty
    }
  }, [userId, collectionName]);
  
  

// Load all available fields initially and add them to selectedFields
useEffect(() => {
  if (Object.keys(adjustmentValues).length > 0 && selectedFields.length === 0) {
    const initialFields = Object.keys(adjustmentValues);
    setSelectedFields(initialFields); // Initialize with all fields from adjustmentValues
  }
}, [adjustmentValues]);


useEffect(() => {
  const fetchAndCalculateNextSummary = async () => {
    setLoadingNextSummary(true); // Set loading state at the start
    try {
      const nextRef = doc(db, 'users', userId, 'Wealth_Building_Stage', collectionName, 'Next', 'data');
      const nextSnapshot = await getDoc(nextRef);

      if (nextSnapshot.exists()) {
        const data = nextSnapshot.data();
        
        // Assuming `totalIncomeMonthly` is either stored in this document or calculated elsewhere
        const totalIncomeMonthly = parseFloat(data.totalIncomeMonthly || 0);
        
        const summaryData = {
          needs: {
            ideal: totalIncomeMonthly * 0.5,
            actual: data.Needs_Necessities ? Object.values(data.Needs_Necessities).reduce((acc, val) => acc + val, 0) : 0,
          },
          wants: {
            ideal: totalIncomeMonthly * 0.3,
            actual: data.Wants_Luxuries ? Object.values(data.Wants_Luxuries).reduce((acc, val) => acc + val, 0) : 0,
          },
          savings: {
            ideal: totalIncomeMonthly * 0.2,
            actual: data.Savings_And_Debt_Repayment ? Object.values(data.Savings_And_Debt_Repayment).reduce((acc, val) => acc + val, 0) : 0,
          },
        };

        setNextSummaryData(summaryData); // Set the calculated data in state
      } else {
        console.warn('No Next data found in Firestore.');
      }
    } catch (error) {
      console.error('Failed to fetch and calculate Next summary data:', error);
    } finally {
      setLoadingNextSummary(false); // Set loading state to false once finished
    }
  };

  fetchAndCalculateNextSummary();
}, [userId, collectionName]);

// useEffect(() => {
//   console.log('Initial Selected Fields:', selectedFields);
// }, [selectedFields]);

  
  return (
    <div>
       <ToastContainer />
       
     {showSummary && summaryData ? (
      <div>
<h1 className="text-4xl text-center font-bold my-4">Financial Plan Summary</h1>

<div className="flex justify-center mb-4">
 <button
   className={`px-4 py-2 font-semibold w-6/12 ${selectedTab === 'Past' ? 'bg-gray-300' : 'bg-gray-100'}`}
   onClick={() => handleTabChange('Past')}
 >
   Past 12 Months
 </button>
 <button
   className={`px-4 py-2 font-semibold w-6/12 ${selectedTab === 'Next' ? 'bg-gray-300' : 'bg-gray-100'}`}
   onClick={() => handleTabChange('Next')}
 >
   Next 12 Months
 </button>
</div>
        <div className="mb-6 bg-gray-100 border-r-8 p-5">
          {Object.keys(allUserData).map((stageKey) => (
            <div key={stageKey}>

              {/* Display Past 12 Months section */}
              {selectedTab === 'Past' && stageKey === 'Past' && (
                <div className="w-full">
                           <h3
                className="text-xl text-center font-semibold cursor-pointer border-b pb-2"
              
              >
                {stageKey === 'Past' ? 'Past 12 Months Table' : 'Next 12 Months Table'}
               
              </h3>
                  {Array.isArray(allUserData['Past']) && allUserData['Past'].length > 0 && (
                    <table className="w-full border-collapse border mt-4">
                      <thead className="bg-white sticky top-0 z-10">
                        <tr>
                          <th className="border px-4 py-2">Income / Expenses</th>
                          <th className="border px-4 py-2">Total (Past 12 Months)</th>
                          <th className="border px-4 py-2">Monthly Average (Past 12 Months)</th>
                          <th className="border px-4 py-2">% of Total (Past 12 Months)</th>
                          <th className="border px-4 py-2">Areas in Need of More Sacrifice & Discipline</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allUserData['Past'].map((docData, index) => (
                          <React.Fragment key={index}>
                            {['Needs_Necessities_', 'Savings_and_Debt_Repayment', 'Wants_Luxuries_'].map(
                              (field) =>
                                docData[field] !== undefined && (
                                  <React.Fragment key={field}>
                                    <tr>
                                      <td colSpan="5" className="text-center font-semibold py-2 bg-gray-200">
                                        {beautifyFieldName(field)}
                                      </td>
                                    </tr>

                                    {Object.keys(docData[field]  || {})
                                      .filter(
                                        (subField) =>
                                          !['totalIncome', 'totalIncomeMonthly', 'expensesMonthly'].includes(subField)
                                      )
                                      .map((subField) => {
                                        const fieldValue = docData[field][subField];
                                        const monthlyAverage = fieldValue / 12;
                                        const percentageOfTotal = (fieldValue / tableData.totalExpenses) * 100;
                                        const fieldKey = `${field.replace(/_$/, '')}.${subField}`;
                                        const isChecked = selectedFields.includes(fieldKey);
                                        //console.log('Checkbox Field Key:', fieldKey, 'Is Checked:', isChecked);
                                        // console.log('Generated field key:', fieldKey);
                                        // console.log('Is checked:', selectedFields.includes(fieldKey));
                                        return (
                                          <tr key={subField}>
                                            <td className="border px-4 py-2">{beautifyFieldName(subField)}</td>
                                            <td className="border px-4 py-2">
                                              {typeof fieldValue === 'number'
                                                ? `$${fieldValue.toLocaleString(undefined, {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                  })}`
                                                : fieldValue}
                                            </td>
                                            <td className="border px-4 py-2">
                                              {typeof fieldValue === 'number'
                                                ? `$${monthlyAverage.toLocaleString(undefined, {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                  })}`
                                                : '-'}
                                            </td>
                                            <td className="border px-4 py-2">
                                              {typeof fieldValue === 'number'
                                                ? `${percentageOfTotal.toFixed(2)}%`
                                                : '-'}
                                            </td>
                                            <td className="border px-4 py-2">
                                              <input
                                                type="checkbox"
                                                checked={isChecked}
                                                onChange={() => handleCheckboxChange(fieldKey)}
                                              />
                                            </td>
                                          </tr>
                                        );
                                      })}

                                    <tr className="font-bold">
                                      <td className="border px-4 py-2">
                                        Total spent on {beautifyFieldName(field)}
                                      </td>
                                      <td className="border px-4 py-2">$ {tableData.totalExpenses}</td>
                                      <td className="border px-4 py-2">{tableData.expensesMonthly}</td>
                                      <td className="border px-4 py-2">
                                        {tableData.totalExpensesPercentage > 100
                                          ? '100%'
                                          : `${tableData.totalExpensesPercentage}%`}
                                      </td>
                                    </tr>
                                  </React.Fragment>
                                )
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  )}

{loadingSummary ? (
                <div>Loading summary data...</div>
              ) : summaryData ? (
                <div className="w-full border-collapse border mt-4 bg-white">
                <h3 className="text-1xl text-center font-bold my-4"> Areas in Need of More Sacrifice & Discipline Summary for Past 12 Months Assessment                                 </h3>
                <table className="w-full border-collapse border mt-4 bg-white">
                  <thead>
                    <tr>
                      <th className="border px-4 py-2">Area of Monthly Spending</th>
                      <th className="border px-4 py-2">50/30/20 Rule Max/Min Ideal Monthly</th>
                      <th className="border px-4 py-2">Actual Amount Spent</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border px-4 py-2">Needs/Necessities</td>
                      <td className="border px-4 py-2">${parseFloat(summaryData.needs.ideal).toFixed(2)}</td>
                      <td className="border px-4 py-2">${parseFloat(summaryData.needs.actual).toFixed(2)}</td>
                    </tr>
                    <tr>
                      <td className="border px-4 py-2">Wants/Luxuries</td>
                      <td className="border px-4 py-2">${parseFloat(summaryData.wants.ideal).toFixed(2)}</td>
                      <td className="border px-4 py-2">${parseFloat(summaryData.wants.actual).toFixed(2)}</td>
                    </tr>
                    <tr>
                      <td className="border px-4 py-2">Savings & Debt Repayment</td>
                      <td className="border px-4 py-2">${parseFloat(summaryData.savings.ideal).toFixed(2)}</td>
                      <td className="border px-4 py-2">${parseFloat(summaryData.savings.actual).toFixed(2)}</td>
                    </tr>
                  </tbody>
                </table>
                </div>
              ) : (
                <div>No summary data available.</div>
              )}
   
{/*  
          <div className="flex justify-center mt-8 ">
          <button
                type="button"
                onClick={handleAdjustFields}
                className="px-8 py-2 bg-green-500 text-white font-bold rounded-full hover:bg-green-600"
              >
                Adjust Areas in Need of More Sacrifice & Discipline
              </button>
              </div> */}

                </div>
              )}

              {/* Display Next 12 Months section */}
              {selectedTab === 'Next' && stageKey === 'Next' && (
                <div className="w-full">
                                     <h3
                className="text-xl text-center font-semibold cursor-pointer border-b pb-2"
              
              >
                {stageKey === 'Past' ? 'Past 12 Months Table' : 'Next 12 Months Table'}
               
              </h3>
              {Object.keys(adjustmentValues).length > 0 ? (

                    <table className="w-full border-collapse border mt-4">
                      <thead className="bg-white sticky top-0 z-10">
                        <tr>
                          <th className="border px-4 py-2">Field</th>
                          <th className="border px-4 py-2">Total (Next 12 Months)</th>
                          <th className="border px-4 py-2">Monthly Average (Next 12 Months)</th>
                          <th className="border px-4 py-2">% of Total (Next 12 Months)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* Grouping by categories like "Needs Necessities", "Wants Luxuries" */}
                        {['Needs_Necessities', 'Savings_And_Debt_Repayment', 'Wants_Luxuries'].map((category) => {
             
                          
                     const filteredFields = Object.keys(adjustmentValues).filter((field) => field.startsWith(category));

                        if (filteredFields.length === 0) return null;

                            
                        // const filteredFields = Object.keys(adjustmentValues).filter((field) => field.startsWith(category));
                        // if (filteredFields.length === 0) return null;

                          return (

                            <React.Fragment key={category}>
                              <tr>
                                <td colSpan="4" className="text-center font-semibold py-2 bg-gray-200">
                                  {beautifyFieldName(category)}
                                </td>
                              </tr>
                              {filteredFields.map((field) => {
                                // If no data exists for this field, default to an empty string
                                const fieldValue = adjustmentValues[field]?.value || '';
                                const monthlyAverage = fieldValue ? fieldValue / 12 : 0;
                                const percentageOfTotal = fieldValue && tableData.totalExpenses > 0
                                  ? (fieldValue / tableData.totalExpenses) * 100
                                  : 0;

      
                                  console.log("Rendering adjustmentValues for field:", field, adjustmentValues[field]);
      
         

                                return (
                                  <tr key={field}>
                                    <td className="border px-4 py-2">{beautifyFieldName(field.split('.')[1])}</td>
                                    <td className="border px-4 py-2">
                                      <input
                                        type="number"
                                        name={field}
                                        value={fieldValue}
                                        onChange={handleAdjustmentChange}
                                        className="w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                      />
                                    </td>
                                    <td className="border px-4 py-2">
                                      {monthlyAverage.toLocaleString(undefined, {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      })}
                                    </td>
                                    <td className="border px-4 py-2">
                                      {percentageOfTotal > 100 ? '100%' : `${percentageOfTotal.toFixed(2)}%`}
                                    </td>
                                  </tr>
                                );
                              })}
                               {/* Total row for each category */}
                                {filteredFields.length > 0 && (
                                  <tr className="font-bold">
                                    <td className="border px-4 py-2">Total spent on {beautifyFieldName(category)}</td>
                                    <td className="border px-4 py-2">
                                      $ {calculateTotalForSelectedFields(category).total}
                                    </td>
                                    <td className="border px-4 py-2">
                                      $ {calculateTotalForSelectedFields(category).totalMonthly}
                                    </td>
                                    <td className="border px-4 py-2">
                                      {calculateTotalForSelectedFields(category).totalPercentage > 100
                                        ? '100%'
                                        : `${calculateTotalForSelectedFields(category).totalPercentage}%`}
                                    </td>
                                  </tr>
                                )}
                                            </React.Fragment>
                          );
                        })}
                        
                      </tbody>
                    </table>
                    
                  ) : (
      <div>No data available for Next 12 Months.</div>
    )}

                  {/* Save button for the Next 12 Months table */}
                  {selectedFields.length > 0 && allUserData['Next'].length > 0 && (
                    
                    <div className="flex justify-center mt-4">
                      <button
                        type="button"
                        onClick={saveAdjustments} // Trigger saveAdjustments function
                        className="px-8 py-2 bg-green-500 text-white font-bold rounded-full hover:bg-green-600"
                      >
                        Save Adjustments
                      </button>
                    </div>
                  )}
                  


    {/* Add summary section for 'Next' data */}
    {loadingNextSummary ? (
      <div>Loading next summary data...</div>
    ) : nextSummaryData ? (
      <div className="w-full border-collapse border mt-4 bg-white">
        <h3 className="text-1xl text-center font-bold my-4">Areas in Need of More Sacrifice & Discipline Summary for Next 12 Months Assessment</h3>
        <table className="w-full border-collapse border mt-4 bg-white">
          <thead>
            <tr>
              <th className="border px-4 py-2">Area of Monthly Spending</th>
              <th className="border px-4 py-2">50/30/20 Rule Max/Min Ideal Monthly</th>
              <th className="border px-4 py-2">Actual Amount
              Spent</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border px-4 py-2">Needs/Necessities</td>
              <td className="border px-4 py-2">${parseFloat(nextSummaryData.needs.ideal).toFixed(2)}</td>
              <td className="border px-4 py-2">${parseFloat(nextSummaryData.needs.actual).toFixed(2)}</td>
            </tr>
            <tr>
              <td className="border px-4 py-2">Wants/Luxuries</td>
              <td className="border px-4 py-2">${parseFloat(nextSummaryData.wants.ideal).toFixed(2)}</td>
              <td className="border px-4 py-2">${parseFloat(nextSummaryData.wants.actual).toFixed(2)}</td>
            </tr>
            <tr>
              <td className="border px-4 py-2">Savings & Debt Repayment</td>
              <td className="border px-4 py-2">${parseFloat(nextSummaryData.savings.ideal).toFixed(2)}</td>
              <td className="border px-4 py-2">${parseFloat(nextSummaryData.savings.actual).toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    ) : (
      <div>No summary data available for Next 12 Months.</div>
    )}

                </div>
                
              )}
            </div>
          ))}
        </div>

              {showAdjustmentForm && (
                <div className="fixed inset-0 z-10 overflow-y-auto">
                  <div className="flex items-center justify-center min-h-screen px-4">
                    <div className="relative z-10 w-full max-w-lg p-6 bg-white rounded-lg shadow-md">
                      {/* Close Button */}
                      <button onClick={() => setShowAdjustmentForm(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
                        ✖
                      </button>

                      {/* Modal Title */}
                      <h2 className="text-xl font-bold mb-2 text-center">
                        Adjust Areas in Need of More Sacrifice & Discipline
                      </h2>

                      {/* Render Adjustment Form */}
                      <AdjustmentForm
                        adjustmentValues={adjustmentValues}
                        setAdjustmentValues={setAdjustmentValues}
                        saveAdjustments={saveAdjustments}
                      />
                    </div>

                    {/* Modal Backdrop */}
                    <div className="fixed z-0 inset-0 bg-black opacity-50"></div>
                  </div>
                </div>
              )}




          <div className="flex justify-center mt-8 ">
            <button type="button" onClick={hideSummary} className="px-8 py-2 mr-2 bg-gray-500 text-white font-bold rounded-full hover:bg-gray-600">
              Back
            </button>
            <button
              onClick={handleRedirectToStart}
              className="px-8 py-2 bg-yellow-500 text-black font-bold rounded-full hover:bg-yellow-600"
            >
              Select Next Stage
            </button>
    
          </div>

 
        </div>
      ) : (
        <>
          {showReviewPopup && (
            <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 p-4 bg-white border border-gray-300 shadow-lg rounded-md z-50">
              <h4 className="text-red-600">
                <strong>Please fill in all the fields before submitting.</strong>
              </h4>
              <p>Some of your responses were incorrect.</p>
            </div>
          )}
          <StepHeader stepTitle={currentStepData.stepTitle} stepDescription={currentStepData.stepDescription} />
          <form onSubmit={(e) => handleSubmit(e, nextStep)} className="userForm" id="wealthForm">
            {currentStep === 22 ? (
              <>
                <div className="w-full">
                  <div className="hidden md:grid grid-cols-4 gap-8">
                    <div>
                      <p><strong>Income</strong></p>
                      <label>After-tax salary or wages</label>
                      <label className="mt-8 block">Any additional income (rental, financial aid, self-employment, child support, pension, etc.)</label>
                    </div>
                    <div>
                      <p><strong>Total (Past 12 Months)</strong></p>
                      <input
                      type="number"
                      name="incomeAfterTax"
                      className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                      value={formValues.incomeAfterTax || ''} // Ensure correct binding to formValues
                      onChange={handleChange}
                    />
                     <input
                      type="number"
                      name="additionalIncome"
                      className="mt-5 shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                      value={formValues.additionalIncome || ''} // Ensure correct binding to formValues
                      onChange={handleChange}
                    />
                    </div>
                    <div>
                      <p><strong>Monthly Average (Past 12 Months)</strong></p>
                      <p className="mt-3">{tableData.incomeAfterTaxMonthly}</p>
                      <p className="mt-8">{tableData.additionalIncomeMonthly}</p>
                    </div>
                    <div>
                      <p><strong>% of Total (Past 12 Months)</strong></p>
                      <p className="mt-3">{tableData.incomeAfterTaxPercentage}%</p>
                      <p className="mt-8">{tableData.additionalIncomePercentage}%</p>
                    </div>
                  </div>

                  <div className="hidden md:grid grid-cols-4 gap-8 mt-10">
                    <div><strong>Total income</strong></div>
                    <div><strong>{tableData.totalIncome}</strong></div>
                    <div id="totalIncomeMonthly"><strong>{tableData.totalIncomeMonthly}</strong></div>
                    <div><strong>{tableData.totalIncomePercentage}%</strong></div>
                  </div>

                  <div className="md:hidden flex flex-col space-y-4">
                    <div className="bg-white shadow rounded-lg p-4">
                      <h3 className="font-semibold text-lg">Income</h3>
                      <div className="mt-4">
                        <label className="block text-gray-700">After-tax salary or wages</label>
                        <input
                          type="number"
                          name="incomeAfterTax"
                          className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          value={formValues.incomeAfterTax || ''}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="mt-4">
                        <label className="block text-gray-700">Any additional income (rental, financial aid, self-employment, child support, pension, etc.)</label>
                        <input
                          type="number"
                          name="additionalIncome"
                          className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          value={formValues.additionalIncome || ''}
                          onChange={handleChange}
                        />
                      </div>
                    </div>

                    <div className="bg-white shadow rounded-lg p-4">
                      <h3 className="font-semibold text-lg">Totals</h3>
                      <div className="mt-4 flex justify-between">
                        <span className="text-gray-600">Total (Past 12 Months)</span>
                        <span className="font-semibold">{tableData.totalIncome}</span>
                      </div>
                      <div className="mt-2 flex justify-between">
                        <span className="text-gray-600">Monthly Average (Past 12 Months)</span>
                        <span className="font-semibold">{tableData.totalIncomeMonthly}</span>
                      </div>
                      <div className="mt-2 flex justify-between">
                        <span className="text-gray-600">% of Total (Past 12 Months)</span>
                        <span className="font-semibold">{tableData.totalIncomePercentage}%</span>
                      </div>
                    </div>

                    <div className="bg-white shadow rounded-lg p-4">
                      <h3 className="font-semibold text-lg">Breakdown</h3>
                      <div className="mt-4 flex justify-between">
                        <span className="text-gray-600">Monthly Average (After-tax salary)</span>
                        <span className="font-semibold">{tableData.incomeAfterTaxMonthly}</span>
                      </div>
                      <div className="mt-2 flex justify-between">
                        <span className="text-gray-600">Monthly Average (Additional income)</span>
                        <span className="font-semibold">{tableData.additionalIncomeMonthly}</span>
                      </div>
                      <div className="mt-2 flex justify-between">
                        <span className="text-gray-600">% of Total (After-tax salary)</span>
                        <span className="font-semibold">{tableData.incomeAfterTaxPercentage}%</span>
                      </div>
                      <div className="mt-2 flex justify-between">
                        <span className="text-gray-600">% of Total (Additional income)</span>
                        <span className="font-semibold">{tableData.additionalIncomePercentage}%</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-between mt-4">
                  <button type="button" onClick={handleBack} className="px-8 py-2 bg-gray-500 text-white font-bold rounded-full hover:bg-gray-600">
                    Back
                  </button>
                  <button type="submit" className="px-8 py-2 bg-yellow-500 text-black font-bold rounded-full hover:bg-yellow-600">
                    {isLastStep ? 'See Summary' : 'Save / Continue'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <table className="w-full border-collapse hidden md:block">
                  <thead>
                    <tr>
                      <th className="border px-4 py-2">Expenses</th>
                      <th className="border px-4 py-2">Total (Past 12 Months)</th>
                      <th className="border px-4 py-2">Monthly Average (Past 12 Months)</th>
                      <th className="border px-4 py-2">% of Total (Past 12 Months)</th>
                    </tr>
                  </thead>
                  <tbody>
                  {currentStepData.formFields.map((field) => (
                      <tr key={field.id}>
                        <td className="border px-4 py-2">{field.title}</td>
                        <td className="border px-4 py-2">
                          <input
                            type={field.type}
                            name={field.id}
                            className="w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                            value={formValues[field.id] !== undefined ? formValues[field.id] : ''} // Ensure correct binding to formValues
                            onChange={handleChange}
                          />
                        </td>
                        <td className="border px-4 py-2">
                          {formValues[field.id] && `$ ${(parseFloat(formValues[field.id]) / 12).toFixed(2)}`}
                        </td>
                        <td className="border px-4 py-2">
                          {formValues[field.id] && `${((parseFloat(formValues[field.id]) / parseFloat(tableData.totalExpenses)) * 100).toFixed(2)}%`}
                        </td>
                      </tr>
                    ))}


                    <tr className="font-bold">
                      <td className="border px-4 py-2">Total spent on {currentStepData.stepTitle}</td>
                      <td className="border px-4 py-2">$ {tableData.totalExpenses}</td>
                      <td className="border px-4 py-2">{tableData.expensesMonthly}</td>
                      <td className="border px-4 py-2">
                        {tableData.totalExpensesPercentage}%
                      </td>
                    </tr>
                  </tbody>
                </table>

                <div className="md:hidden flex flex-col space-y-4">
                  {currentStepData.formFields.map((field) => (
                    <div key={field.id} className="bg-white shadow rounded-lg p-4">
                      <label className="block text-gray-700">{field.title}</label>
                      <input
                        type="number"
                        name={field.id}
                        className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                        value={formValues[field.id] || ''}
                        onChange={handleChange}
                      />
                    </div>
                  ))}
                </div>

                <div className="flex justify-start mt-4">
                  <button type="button" onClick={handleBack} className="px-8 py-2 bg-gray-500 text-white font-bold rounded-full hover:bg-gray-600">
                    Back
                  </button>
                  <button type="submit" className="px-8 py-2 bg-yellow-500 text-black font-bold rounded-full hover:bg-yellow-600">
                    {isLastStep ? 'See Summary' : 'Save / Continue'}
                  </button>
                </div>
              </>
            )}
          </form>
        </>
      )}
    </div>
  );
};

export default WealthBuildingForm;